/**
 * Tela de Inventário/Equipamento (mobile, touch). Lista com filtros + scroll, e painel
 * de detalhe com comparação (velho → novo), atributos, DPS, maestria e ações
 * (Equipar/Desequipar/Evoluir/Converter). Pausa o jogo enquanto aberta.
 */
import { GameConfig } from "../config/GameConfig";
import { i18n } from "../i18n/i18n";
import { definitionById, isWeaponDef } from "../items/Catalog";
import { rarityCss, rarityIndex } from "../items/Rarity";
import { weaponDps, weaponBaseDamage, armorBaseOf, effectiveRollValue } from "../items/Stats";
import { itemName, rarityName, attrLabel, formatAttrValue, nextMasteryReward } from "../items/format";
import { fmtNum } from "../utils/format";
import { itemSvg, slotSvg } from "../items/icons";
import type { Inventory } from "../items/Inventory";
import type { EquipmentInstance, EquipmentSlot, WeaponType } from "../items/EquipmentTypes";
import type { CharacterData } from "./CharacterScreen";
import { renderAttributes, bindAttributeHelp } from "./AttributesView";
import { reducedMotion } from "./motion";

type Filter = "all" | "weapon" | "head" | "chest" | "gloves" | "boots" | "accessory";
const FILTERS: Filter[] = ["all", "weapon", "head", "chest", "gloves", "boots", "accessory"];

export interface InventoryHandlers {
  onEquip: (id: string) => void;
  onUnequip: (slot: EquipmentSlot) => void;
  onUpgrade: (id: string, useElixir: boolean) => { text: string; kind: "ok" | "bad" | "info" } | void; // 10.9P/BH — devolve o resultado pra tela mostrar
  getElixirs: () => number; // 10.9P — quantos elixires de proteção o jogador tem
  getCoins: () => number; // 10.9BI — saldo de 🪙 (a forja custa Fragmentos além dos shards)
  onConvert: (id: string) => void;
  onSell: (ids: string[]) => void; // 10.9M — vender vários itens da bolsa por 🪙
  onSetAutoSell: (rarity: string) => void; // 10.9M — define a raridade-teto da auto-venda
  getAutoSell: () => string; // 10.9M — raridade-teto atual ("off" = desligada)
  getChainedIds: () => string[]; // 10.9L17 — armas presas na corrente agora (não podem ser vendidas)
  onEquipBest: () => void; // 10.9HV — equipar a MELHOR arma entre TODOS os tipos da classe (era por tipo)
  onShop: () => void; // 10.5I: Loja mora dentro da Bolsa
  onWardrobe: () => void; // 10.5I: Guarda-roupa mora dentro da Bolsa
  onAttributes: () => CharacterData; // 10.5K: ❗ ATRIBUTOS completos (dados prontos do Game)
  onClose: () => void;
}

/** Ícone do filtro "todos" (grade). */
const ALL_SVG = `<svg viewBox="0 0 24 24" width="100%" height="100%" style="display:block;opacity:0.75"><rect x="3" y="3" width="7.5" height="7.5" rx="1.6" fill="currentColor"/><rect x="13.5" y="3" width="7.5" height="7.5" rx="1.6" fill="currentColor"/><rect x="3" y="13.5" width="7.5" height="7.5" rx="1.6" fill="currentColor"/><rect x="13.5" y="13.5" width="7.5" height="7.5" rx="1.6" fill="currentColor"/></svg>`;
/** Silhueta do Caçador no centro do "paper doll" (estilo Tibia). */
// 10.9FS — o boneco da bolsa agora é O SEU caçador: capa/ombreiras/visor na COR DA CLASSE.
// Os slots ao redor mostram cada peça equipada; a ARMA aparece na mão (overlay em renderDoll).
// 10.9GX — o boneco SVG deu lugar à PRÉVIA 3D (canvas .pd-3d): o Caçador real, com arma e armadura.
/** 10.9GZ — selo "T7" do TIER (a escada de poder dos itens de boss / armadura) nos cards e no boneco. */
function tierBadge(inst: EquipmentInstance, cls = "tier"): string {
  const t = (definitionById(inst.definitionId) as { tier?: number } | undefined)?.tier;
  return t && t >= 2 ? `<span class="${cls}">T${t}</span>` : "";
}

export class InventoryScreen {
  private readonly root: HTMLDivElement;
  private readonly grid: HTMLElement;
  private readonly detail: HTMLElement;
  private readonly listView: HTMLElement;
  private readonly attrsView: HTMLElement; // 10.5K: painel ❗ ATRIBUTOS (sobre a lista)
  private readonly forgeView: HTMLElement; // 10.7F: painel FORJA (upgrade de qualquer item)
  private readonly sellView: HTMLElement; // 10.9M: painel VENDER (venda em massa + auto-venda)
  private inv: Inventory | null = null;
  private filter: Filter = "all";
  private selectedId: string | null = null;
  private sellSel = new Set<string>(); // 10.9M — itens marcados para vender
  private forgeProtect = true; // 10.9P — usar elixir pra proteger da queda (se tiver)

  constructor(uiRoot: HTMLElement, private readonly h: InventoryHandlers) {
    this.root = document.createElement("div");
    this.root.className = "overlay inv-overlay";
    this.root.hidden = true;
    this.root.innerHTML = `
      <div class="panel inv-panel">
        <div class="inv-head"><h2 class="inv-title"></h2><button class="btn inv-x" data-a="close">✕</button></div>
        <div class="inv-list">
          <div class="inv-summary"></div>
          <div class="inv-hub">
            <button class="btn inv-hub-btn attrs" data-a="attrs">❗ <span></span></button>
            <button class="btn inv-hub-btn forge" data-a="forge">🔨 <span></span></button>
            <button class="btn inv-hub-btn sell" data-a="sell">💰 <span></span></button>
            <button class="btn inv-hub-btn" data-a="wardrobe">🎨 <span></span></button>
          </div>
          <div class="inv-doll"></div>
          <div class="inv-types">
            <button class="inv-type" data-a="equip-best"><span class="it-ic">⚔️</span><span class="it-lbl"></span></button>
          </div>
          <div class="inv-filters">
            ${FILTERS.map((f) => `<button class="fbtn" data-f="${f}" title=""><span class="fb-ic">${f === "all" ? ALL_SVG : slotSvg(f)}</span></button>`).join("")}
          </div>
          <div class="inv-grid"></div>
        </div>
        <div class="inv-detail" hidden></div>
        <div class="inv-attrs" hidden></div>
        <div class="inv-forge" hidden></div>
        <div class="inv-sell" hidden></div>
      </div>`;
    uiRoot.appendChild(this.root);
    this.grid = this.root.querySelector(".inv-grid")!;
    this.detail = this.root.querySelector(".inv-detail")!;
    this.listView = this.root.querySelector(".inv-list")!;
    this.attrsView = this.root.querySelector(".inv-attrs")!;
    this.forgeView = this.root.querySelector(".inv-forge")!;
    this.sellView = this.root.querySelector(".inv-sell")!;

    // 10.9W — botão do canto é CONTEXTUAL: no detalhe de um item vira "‹ Voltar" (volta pra
    // lista); só na lista ele fecha a bolsa (fechar fora da área de seleção, pedido do Thiago).
    this.root.querySelector('[data-a="close"]')!.addEventListener("click", () => {
      if (this.selectedId) { this.selectedId = null; this.render(); }
      else this.h.onClose();
    });
    this.root.querySelector('[data-a="wardrobe"]')!.addEventListener("click", () => this.h.onWardrobe());
    this.root.querySelector('[data-a="attrs"]')!.addEventListener("click", () => this.openAttributes());
    this.root.querySelector('[data-a="forge"]')!.addEventListener("click", () => this.openForge());
    this.root.querySelector('[data-a="sell"]')!.addEventListener("click", () => this.openSell());
    this.root.querySelector('[data-a="equip-best"]')!.addEventListener("click", () => this.h.onEquipBest());
    for (const f of FILTERS) {
      this.root.querySelector<HTMLButtonElement>(`.fbtn[data-f="${f}"]`)!.onclick = () => {
        this.filter = f;
        this.selectedId = null;
        this.render();
      };
    }
    i18n.onChange(() => { if (!this.root.hidden) this.render(); });
  }

  private playerLevel = 1;

  open(inv: Inventory, focusSlot?: EquipmentSlot, playerLevel = 1, _lookColor?: string): void {
    this.inv = inv;
    this.playerLevel = playerLevel;
    this.filter = focusSlot ?? "all";
    this.selectedId = focusSlot ? inv.equipped[focusSlot]?.instanceId ?? null : null;
    this.attrsView.hidden = true;
    this.forgeView.hidden = true;
    this.sellView.hidden = true;
    this.sellSel.clear();
    this.root.hidden = false;
    this.render();
  }
  hide(): void { this.root.hidden = true; }
  /** 10.9HK — dano da arma na CORRENTE (fornecido pelo Game); aparece no card das armas não equipadas. */
  private chainDmg: ((inst: EquipmentInstance) => number) | null = null;
  setChainPreview(fn: (inst: EquipmentInstance) => number): void { this.chainDmg = fn; }
  /** 10.9GX — prévia 3D: a tela está aberta? / canvas de saída / ângulo (gira sozinho; arrastar gira) */
  get isOpen(): boolean { return !this.root.hidden; }
  get previewCanvas(): HTMLCanvasElement | null { return this.root.querySelector<HTMLCanvasElement>(".pd-3d"); }
  private previewAng = 0.6;
  private previewDrag: { x: number; on: boolean } = { x: 0, on: false };
  private previewIdle = 0;
  tickPreview(dt: number): number {
    if (!this.previewDrag.on) { this.previewIdle += dt; if (this.previewIdle > 1.2 && !reducedMotion()) this.previewAng += dt * 0.55; }
    return this.previewAng;
  }
  private bindPreview(cv: HTMLCanvasElement): void {
    cv.onpointerdown = (e) => { this.previewDrag = { x: e.clientX, on: true }; cv.setPointerCapture(e.pointerId); };
    cv.onpointermove = (e) => { if (!this.previewDrag.on) return; this.previewAng += (e.clientX - this.previewDrag.x) * 0.012; this.previewDrag.x = e.clientX; };
    cv.onpointerup = cv.onpointercancel = () => { this.previewDrag.on = false; this.previewIdle = 0; };
  }
  refresh(playerLevel?: number): void {
    if (typeof playerLevel === "number") this.playerLevel = playerLevel;
    if (!this.root.hidden) this.render();
  }

  /** 10.5K: ❗ ATRIBUTOS — lista completa sobre a Bolsa (volta para a lista com ←). */
  private openAttributes(): void {
    this.attrsView.innerHTML = renderAttributes(this.h.onAttributes());
    bindAttributeHelp(this.attrsView); // 10.9ER — tocar no atributo explica o que ele faz
    this.attrsView.hidden = false;
    this.listView.hidden = true;
    this.detail.hidden = true;
    this.attrsView.querySelector('[data-a="attr-back"]')!.addEventListener("click", () => {
      this.attrsView.hidden = true;
      this.render();
    });
    this.attrsView.scrollTop = 0;
  }

  /** 10.7F — FORJA: mostra TODOS os itens (bolsa + equipados) com botão de upgrade em cada. */
  private openForge(): void {
    this.forgeView.hidden = false;
    this.listView.hidden = true;
    this.detail.hidden = true;
    this.attrsView.hidden = true;
    this.renderForge();
    this.forgeView.scrollTop = 0;
  }

  private renderForge(): void {
    const inv = this.inv;
    if (!inv) return;
    const items: EquipmentInstance[] = [];
    for (const slot of ["weapon","head","chest","gloves","boots","accessory"] as EquipmentSlot[]) {
      const eq = inv.equipped[slot];
      if (eq) items.push(eq);
    }
    for (const it of inv.bag) items.push(it);
    // ordena: pode upgradar > shards+cost menor > raridade maior
    items.sort((a, b) => {
      const ua = inv.canUpgrade(a) ? 0 : 1;
      const ub = inv.canUpgrade(b) ? 0 : 1;
      if (ua !== ub) return ua - ub;
      const costA = inv.upgradeCost(a);
      const costB = inv.upgradeCost(b);
      return costA - costB;
    });
    const maxLv = GameConfig.equipment.upgradeMaxLevel;
    const elixirs = this.h.getElixirs();
    const myCoins = this.h.getCoins(); // 10.9BI
    const protectOn = this.forgeProtect && elixirs > 0;
    // barra do elixir: só oferece o toggle quando o jogador TEM elixir
    const elixirBar = elixirs > 0
      ? `<div class="forge-elixir">
           <span>🧪 ${this.esc(i18n.t("forge.elixirsHave", { n: String(elixirs) }))}</span>
           <button class="forge-protect${protectOn ? " on" : ""}" data-a="protect">${protectOn ? "☑" : "☐"} ${this.esc(i18n.t("forge.protect"))}</button>
         </div>`
      : `<div class="forge-elixir dim">🧪 ${this.esc(i18n.t("forge.noElixir"))}</div>`;
    let html = `
      <div class="at-top">
        <button class="btn" data-a="forge-back">←</button>
        <h3 class="at-title">🔨 ${this.esc(i18n.t("forge.title"))}</h3>
      </div>
      <div class="forge-hint">${this.esc(i18n.t("forge.hint"))}</div>
      <div class="forge-wallet">🪙 ${myCoins}</div>
      <div class="forge-toast" hidden></div>
      ${elixirBar}
      <div class="forge-list">`;
    if (items.length === 0) {
      html += `<div class="inv-empty">${this.esc(i18n.t("forge.empty"))}</div>`;
    }
    for (const inst of items) {
      const c = rarityCss(inst.rarity);
      const def = definitionById(inst.definitionId);
      if (!def) continue;
      const shards = inv.getShards(inst.definitionId);
      const cost = inv.upgradeCost(inst);
      const coinCost = inv.upgradeCoins(inst); // 10.9BI
      const isMax = inst.level >= maxLv;
      const canUp = !isMax && shards >= cost && myCoins >= coinCost;
      const coinHtml = isMax ? "" : `<span class="${myCoins >= coinCost ? "forge-coin" : "forge-coin lack"}">🪙 ${coinCost}</span>`;
      const brk = Math.round(inv.breakChance(inst) * 100);
      const isEq = inv.equipped[inst.slot]?.instanceId === inst.instanceId;
      const eqTag = isEq ? `<span class="forge-eq">${this.esc(i18n.t("equipment.equipped"))}</span>` : "";
      const inChain = !isEq && this.h.getChainedIds().includes(inst.instanceId); // 10.9L18
      const chainTag = inChain ? `<span class="forge-eq chain">⛓ ${this.esc(i18n.t("equipment.inChain"))}</span>` : "";
      // aviso de quebra (verde=seguro, laranja=risco). Com proteção ligada, mostra "protegido".
      const riskHtml = isMax ? "" : brk <= 0
        ? `<span class="forge-safe">${this.esc(i18n.t("forge.safe"))}</span>`
        : protectOn
          ? `<span class="forge-prot">${this.esc(i18n.t("forge.protected", { p: String(brk) }))}</span>`
          : `<span class="forge-risk">${this.esc(i18n.t("forge.risk", { p: String(brk) }))}</span>`;
      const btnHtml = isMax
        ? `<button class="btn forge-btn dim" disabled>${this.esc(i18n.t("forge.max"))}</button>`
        : `<button class="btn forge-btn ${canUp ? "primary" : "dim"}" data-id="${inst.instanceId}" ${canUp ? "" : "disabled"}>${this.esc(i18n.t("forge.upgradeTo", { n: String(inst.level + 1) }))}</button>`;
      html += `
        <div class="forge-row" style="border-color:${c};--rc:${c}">
          <div class="forge-icon">${itemSvg(inst)}</div>
          <div class="forge-info">
            <div class="forge-name" style="color:${c}">${this.esc(itemName(inst))} ${eqTag}${chainTag}</div>
            <div class="forge-sub">${this.esc(rarityName(inst))} · ${this.esc(i18n.t("slot." + def.slot))} · L${inst.level}/${maxLv}</div>
            <div class="forge-cost">${isMax ? "" : `✨ ${shards} / ${cost}`} ${coinHtml} ${riskHtml}</div>
          </div>
          ${btnHtml}
        </div>`;
    }
    html += `</div>`;
    this.forgeView.innerHTML = html;
    this.forgeView.querySelector('[data-a="forge-back"]')!.addEventListener("click", () => {
      this.forgeView.hidden = true;
      this.render();
    });
    this.forgeView.querySelector('[data-a="protect"]')?.addEventListener("click", () => { this.forgeProtect = !this.forgeProtect; this.renderForge(); });
    this.forgeView.querySelectorAll<HTMLButtonElement>(".forge-btn[data-id]").forEach(btn => {
      btn.addEventListener("click", () => {
        const id = btn.dataset.id!;
        const r = this.h.onUpgrade(id, this.forgeProtect && this.h.getElixirs() > 0);
        // re-render sem fechar
        if (!this.forgeView.hidden) this.renderForge();
        if (r) this.showForgeToast(r.text, r.kind); // 10.9BH — resultado DENTRO da forja (o banner fica atrás do overlay)
      });
    });
  }

  private forgeToastTimer = 0;
  /** 10.9BH — toast de resultado da forja (sucesso/quebra/salvo), visível por cima da lista. */
  private showForgeToast(text: string, kind: "ok" | "bad" | "info"): void {
    const el = this.forgeView.querySelector<HTMLElement>(".forge-toast");
    if (!el) return;
    el.textContent = text;
    el.className = `forge-toast ${kind}`;
    el.hidden = false;
    window.clearTimeout(this.forgeToastTimer);
    this.forgeToastTimer = window.setTimeout(() => { el.hidden = true; }, 2200);
  }

  /** 10.9M — VENDER: seleção múltipla da bolsa + auto-venda por raridade. */
  private openSell(): void {
    this.sellView.hidden = false;
    this.listView.hidden = true;
    this.detail.hidden = true;
    this.attrsView.hidden = true;
    this.forgeView.hidden = true;
    this.sellSel.clear();
    this.renderSell();
    this.sellView.scrollTop = 0;
  }

  // raridades oferecidas na AUTO-venda (até rara; épico+ o jogador vende na mão de propósito)
  private static readonly AUTO_RARITIES = ["off", "common", "uncommon", "rare"] as const;

  private renderSell(): void {
    const inv = this.inv;
    if (!inv) return;
    const auto = this.h.getAutoSell();
    // limpa da seleção o que não está mais na bolsa (vendido)
    for (const id of [...this.sellSel]) if (!inv.bag.some((i) => i.instanceId === id)) this.sellSel.delete(id);
    // 10.9L17 — arma presa na corrente é equipamento EM USO (ataca sozinha); nunca aparece pra vender.
    const chained = new Set(this.h.getChainedIds());
    for (const id of chained) this.sellSel.delete(id);
    const items = [...inv.bag].filter((i) => !chained.has(i.instanceId)).sort((a, b) => rarityIndex(a.rarity) - rarityIndex(b.rarity)); // pior primeiro
    const total = items.filter((i) => this.sellSel.has(i.instanceId)).reduce((s, i) => s + inv.sellValue(i), 0);

    const autoBtns = InventoryScreen.AUTO_RARITIES.map((r) => {
      const active = auto === r ? " active" : "";
      const lbl = r === "off" ? i18n.t("sell.off") : i18n.t(`rarity.${r}`);
      return `<button class="sell-auto-b${active}" data-r="${r}">${this.esc(lbl)}</button>`;
    }).join("");

    let html = `
      <div class="at-top">
        <button class="btn" data-a="sell-back">←</button>
        <h3 class="at-title">💰 ${this.esc(i18n.t("sell.title"))}</h3>
      </div>
      <div class="forge-hint">${this.esc(i18n.t("sell.hint"))}</div>
      <div class="sell-auto">
        <span class="sell-auto-lbl">${this.esc(i18n.t("sell.auto"))}</span>
        <div class="sell-auto-btns">${autoBtns}</div>
      </div>
      <div class="sell-quick">
        <button class="btn sell-mark" data-a="sell-mark">${this.esc(i18n.t("sell.markLow"))}</button>
        <button class="btn sell-clear" data-a="sell-clear">${this.esc(i18n.t("sell.clear"))}</button>
      </div>
      <div class="forge-list sell-list">`;
    if (items.length === 0) html += `<div class="inv-empty">${this.esc(i18n.t("sell.empty"))}</div>`;
    for (const inst of items) {
      const c = rarityCss(inst.rarity);
      const def = definitionById(inst.definitionId);
      if (!def) continue;
      const on = this.sellSel.has(inst.instanceId);
      html += `
        <div class="forge-row sell-row${on ? " on" : ""}" role="checkbox" tabindex="0" aria-checked="${on}" data-id="${inst.instanceId}" style="border-color:${c};--rc:${c}">
          <div class="sell-check">${on ? "☑" : "☐"}</div>
          <div class="forge-icon">${itemSvg(inst)}</div>
          <div class="forge-info">
            <div class="forge-name" style="color:${c}">${this.esc(itemName(inst))}</div>
            <div class="forge-sub">${this.esc(rarityName(inst))} · ${this.esc(i18n.t("slot." + def.slot))} · L${inst.level}</div>
          </div>
          <div class="sell-val">+${inv.sellValue(inst)} 🪙</div>
        </div>`;
    }
    html += `</div>
      <div class="sell-foot">
        <button class="btn primary sell-go${this.sellSel.size === 0 ? " dim" : ""}" data-a="sell-go"${this.sellSel.size === 0 ? " disabled" : ""}>${this.esc(i18n.t("sell.sellN", { n: String(this.sellSel.size), coins: String(total) }))}</button>
      </div>`;
    this.sellView.innerHTML = html;

    this.sellView.querySelector('[data-a="sell-back"]')!.addEventListener("click", () => { this.sellView.hidden = true; this.render(); });
    this.sellView.querySelectorAll<HTMLButtonElement>(".sell-auto-b").forEach((b) => b.addEventListener("click", () => { this.h.onSetAutoSell(b.dataset.r!); this.renderSell(); }));
    this.sellView.querySelector('[data-a="sell-mark"]')!.addEventListener("click", () => {
      for (const i of inv.bag) if (rarityIndex(i.rarity) <= rarityIndex("uncommon") && !chained.has(i.instanceId)) this.sellSel.add(i.instanceId);
      this.renderSell();
    });
    this.sellView.querySelector('[data-a="sell-clear"]')!.addEventListener("click", () => { this.sellSel.clear(); this.renderSell(); });
    this.sellView.querySelectorAll<HTMLElement>(".sell-row").forEach((row) => {
      row.addEventListener("keydown", (e) => {
        if (e.key !== " " && e.key !== "Enter") return;
        e.preventDefault();
        const id = row.dataset.id!;
        row.click();
        this.sellView.querySelector<HTMLElement>(`.sell-row[data-id="${CSS.escape(id)}"]`)?.focus({ preventScroll: true });
      });
      row.addEventListener("click", () => {
      const id = row.dataset.id!;
      if (this.sellSel.has(id)) this.sellSel.delete(id); else this.sellSel.add(id);
      this.renderSell();
      });
    });
    this.sellView.querySelector('[data-a="sell-go"]')?.addEventListener("click", () => {
      const ids = [...this.sellSel];
      if (ids.length === 0) return;
      this.sellSel.clear();
      this.h.onSell(ids); // Game vende + afterInventoryChange → refresh() → renderSell()
    });
  }

  private esc(s: string): string {
    return s.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");
  }

  private render(): void {
    if (!this.inv) return;
    if (!this.attrsView.hidden) return; // painel de atributos aberto: não mexe na lista
    if (!this.forgeView.hidden) { this.renderForge(); return; } // 10.7F: painel forja aberto
    if (!this.sellView.hidden) { this.renderSell(); return; } // 10.9M: painel vender aberto
    this.root.querySelector(".inv-title")!.textContent = i18n.t("inventory.title");
    // 10.9W — no detalhe o botão do canto vira "‹" (voltar); na lista fica "✕" (fechar)
    const isDetail = Boolean(this.selectedId && this.inv.findAnywhere(this.selectedId));
    this.root.querySelector(".inv-x")!.textContent = isDetail ? "‹" : "✕";
    this.root.querySelector(".inv-x")!.setAttribute("aria-label", i18n.t(isDetail ? "menu.back" : "common.close"));
    this.root.querySelector('[data-a="wardrobe"] span')!.textContent = i18n.t("wardrobe.short");
    this.root.querySelector('[data-a="forge"] span')!.textContent = i18n.t("forge.title");
    this.root.querySelector('[data-a="sell"] span')!.textContent = i18n.t("sell.title");
    this.root.querySelector('[data-a="attrs"] span')!.textContent = i18n.t("attr.title");
    // 10.9HV — 1 único botão "equipar a melhor arma" (era 1 por tipo — Thiago: "faz mais sentido
    // um único botão que equipe as armas mais fortes"). Sempre visível: toda classe usa ≥1 tipo.
    this.root.querySelector('[data-a="equip-best"] .it-lbl')!.textContent = i18n.t("inventory.equipBest");
    for (const f of FILTERS) {
      const b = this.root.querySelector<HTMLButtonElement>(`.fbtn[data-f="${f}"]`)!;
      b.title = f === "all" ? i18n.t("inv.all") : i18n.t(`slot.${f}`); // 10.5Q: só ícone (a bolsa estava poluída)
      b.setAttribute("aria-label", b.title);
      b.classList.toggle("active", this.filter === f);
      b.setAttribute("aria-pressed", String(this.filter === f));
      // 10.9FE — filtro com NOME (antes so icone: ninguem sabia o que era)
      const lbl = b.querySelector(".fb-lbl") ?? b.appendChild(Object.assign(document.createElement("span"), { className: "fb-lbl" }));
      lbl.textContent = b.title;
    }
    const sel = this.selectedId ? this.inv.findAnywhere(this.selectedId) : null;
    if (sel) {
      this.listView.hidden = true;
      this.detail.hidden = false;
      this.renderDetail(sel);
    } else {
      this.selectedId = null;
      this.listView.hidden = false;
      this.detail.hidden = true;
      this.renderList();
    }
  }

  private matchesFilter(inst: EquipmentInstance): boolean {
    return this.filter === "all" || inst.slot === this.filter;
  }

  /**
   * 10.5Q — PAPER DOLL (estilo Tibia): todos os equipamentos ao redor do Caçador, com o
   * ícone/raridade/nível de cada peça. Substituiu a fileira "Equipado", que poluía a lista.
   * Tocar numa peça abre o detalhe (atributos); tocar num slot vazio filtra a bolsa por ele.
   */
  private renderDoll(): void {
    const inv = this.inv!;
    const cell = (s: EquipmentSlot): string => {
      const inst = inv.equipped[s];
      const locked = !inv.isSlotUnlocked(s);
      if (inst) {
        const c = rarityCss(inst.rarity);
        return `<button class="pd-slot filled" data-id="${inst.instanceId}" style="border-color:${c};--rc:${c}" title="${itemName(inst)}">
          <span class="pd-ic">${itemSvg(inst)}</span><span class="pd-lv">L${inst.level}</span>${tierBadge(inst, "pd-tier")}</button>`;
      }
      return `<button class="pd-slot${locked ? " locked" : ""}" data-slot="${s}" title="${i18n.t(`slot.${s}`)}">
        <span class="pd-ic">${slotSvg(s)}</span><span class="pd-nm">${locked ? "🔒" : i18n.t(`slot.${s}`)}</span></button>`;
    };
    const doll = this.root.querySelector<HTMLElement>(".inv-doll")!;
    doll.innerHTML =
      `<div class="pd-col">${cell("head")}${cell("weapon")}${cell("accessory")}</div>` +
      `<div class="pd-body"><canvas class="pd-3d" width="256" height="320" aria-label="preview"></canvas></div>` +
      `<div class="pd-col">${cell("chest")}${cell("gloves")}${cell("boots")}</div>`;
    const cv = doll.querySelector<HTMLCanvasElement>(".pd-3d");
    if (cv) this.bindPreview(cv); // 10.9GX
    doll.querySelectorAll<HTMLButtonElement>(".pd-slot[data-id]").forEach((el) => {
      el.onclick = () => { this.selectedId = el.dataset.id!; this.render(); };
    });
    doll.querySelectorAll<HTMLButtonElement>(".pd-slot[data-slot]").forEach((el) => {
      el.onclick = () => { this.filter = el.dataset.slot as Filter; this.render(); };
    });
  }

  private renderList(): void {
    const inv = this.inv!;
    const items = inv.bag.filter((i) => this.matchesFilter(i));
    this.renderDoll();
    // 10.9L18 — arma da bolsa escolhida pela corrente (ataca sozinha, presa ao Caçador): mostra
    // "Na corrente" igual mostra "Equipado" pra deixar claro que já está em uso.
    const chainedIds = new Set(this.h.getChainedIds());
    const card = (inst: EquipmentInstance, isEq: boolean): string => {
      const c = rarityCss(inst.rarity);
      const need = inv.minLevelToEquip(inst); // 10.9AN — raridade E tier da armadura
      const locked = !isEq && this.playerLevel < need;
      // 10.9FE — MELHOR ou PIOR que o equipado no mesmo lugar (seta verde/vermelha)
      let cmp = "";
      const cur = inv.equipped[inst.slot];
      if (!isEq && cur && cur.instanceId !== inst.instanceId) {
        const def = definitionById(inst.definitionId);
        const a = def && isWeaponDef(def) ? weaponDps(inst) : armorBaseOf(inst).armor;
        const b = def && isWeaponDef(def) ? weaponDps(cur) : armorBaseOf(cur).armor;
        if (a > b * 1.001) cmp = `<span class="cmp-badge up">▲</span>`;
        else if (a < b * 0.999) cmp = `<span class="cmp-badge down">▼</span>`;
      } else if (!isEq && !cur) cmp = `<span class="cmp-badge up">▲</span>`;
      const wdef = definitionById(inst.definitionId);
      const chn = !isEq && this.chainDmg && wdef && isWeaponDef(wdef) ? `<span class="chn">⛓ ${fmtNum(this.chainDmg(inst))}</span>` : ""; // 10.9HK
      // 10.9K9 — ⚗️ = barra de evolução cheia esperando nível (evolui sozinha quando o nível chegar)
      const fus = wdef && isWeaponDef(wdef) && inv.evolveInfo(inst.definitionId, inst.rarity, this.playerLevel).blocked ? `<span class="fuse-badge">⚗️</span>` : "";
      const inChain = !isEq && chainedIds.has(inst.instanceId);
      return `<button class="inv-card${isEq ? " eq" : ""}${locked ? " locked-level" : ""}" data-id="${inst.instanceId}" style="border-color:${c};--rc:${c}">
        <span class="ic">${itemSvg(inst)}</span>
        <span class="lv">L${inst.level}</span>${tierBadge(inst)}${chn}${cmp}${fus}
        ${isEq ? `<span class="tag">${i18n.t("equipment.equipped")}</span>` : ""}
        ${inChain ? `<span class="tag chain">⛓ ${i18n.t("equipment.inChain")}</span>` : ""}
        ${locked ? `<span class="lock-badge">🔒 Nv ${need}</span>` : ""}
        <span class="nm" style="color:${c}">${itemName(inst)}</span>
      </button>`;
    };
    const sumEl = this.root.querySelector<HTMLElement>(".inv-summary")!;
    sumEl.textContent = i18n.t("inventory.storage", { count: inv.bagCount, capacity: inv.capacity });
    // 10.9FE — bolsa LOTADA: aviso vermelho + o botao Vender pulsa
    const full = inv.bagCount >= inv.capacity;
    sumEl.classList.toggle("full", full);
    if (full) sumEl.textContent = `⚠️ ${i18n.t("inv.full")} (${inv.bagCount}/${inv.capacity})`;
    this.root.querySelector('[data-a="sell"]')!.classList.toggle("pulse", full);
    const empty = items.length === 0 ? `<div class="inv-empty">${i18n.t("inventory.bagHint")}</div>` : "";
    const freeSlots = Math.min(4, inv.capacity - inv.bagCount);
    // 10.5Q — só a BOLSA aqui (os equipados agora vivem no paper doll acima)
    this.grid.innerHTML =
      `<h3 class="inv-section">${i18n.t("nav.bag")} <small class="${full ? "full" : ""}">${inv.bagCount}/${inv.capacity}</small></h3>
      ${items.map((i) => card(i, false)).join("")}${empty}
      ${Array.from({ length: freeSlots }, () => `<div class="inv-free-slot" aria-hidden="true">+</div>`).join("")}`;
    this.grid.querySelectorAll<HTMLButtonElement>(".inv-card[data-id]").forEach((el) => {
      el.onclick = () => { this.selectedId = el.dataset.id!; this.render(); };
    });
  }

  private renderDetail(inst: EquipmentInstance): void {
    const inv = this.inv!;
    const def = definitionById(inst.definitionId)!;
    const c = rarityCss(inst.rarity);
    const isWeapon = isWeaponDef(def);
    const equippedInSlot = inv.equipped[inst.slot];
    const isEquipped = equippedInSlot?.instanceId === inst.instanceId;
    const inChain = !isEquipped && this.h.getChainedIds().includes(inst.instanceId); // 10.9L18

    // stats principais + comparação
    let mainLabel: string, mainValue: number, cmpValue: number | null = null;
    if (isWeapon) {
      mainLabel = i18n.t("equipment.dps");
      mainValue = weaponDps(inst);
      cmpValue = !isEquipped && equippedInSlot ? weaponDps(equippedInSlot) : null;
    } else {
      mainLabel = i18n.t("equipment.attr.armor");
      mainValue = armorBaseOf(inst).armor;
      cmpValue = !isEquipped && equippedInSlot ? armorBaseOf(equippedInSlot).armor : null;
    }
    const cmpHtml = cmpValue !== null
      ? `<span class="cmp"><span class="old">${cmpValue}</span> → <span class="new ${mainValue >= cmpValue ? "up" : "down"}">${mainValue}</span></span>`
      : `<span class="new">${mainValue}</span>`;

    // linhas de atributo (base + rolls)
    const lines: string[] = [];
    if (isWeapon) lines.push(`<div class="ln"><span>${i18n.t("equipment.attr.damage")}</span><b>+${weaponBaseDamage(inst)}</b></div>`);
    else {
      const b = armorBaseOf(inst);
      lines.push(`<div class="ln"><span>${i18n.t("equipment.attr.armor")}</span><b>+${b.armor}</b></div>`);
      lines.push(`<div class="ln"><span>${i18n.t("equipment.attr.maxHp")}</span><b>+${b.hp}</b></div>`);
    }
    // 10.9L23 — mostra o valor JÁ escalado pelo nível de forja (o que realmente entra na conta)
    for (const r of inst.rolls) lines.push(`<div class="ln"><span>${attrLabel(r.key)}</span><b>${formatAttrValue(r.key, effectiveRollValue(inst, r.value))}</b></div>`);

    // maestria (armas)
    let masteryHtml = "";
    if (isWeapon) {
      const wt = def.weaponType as WeaponType;
      const ms = inv.masteryState(wt);
      const forNext = inv.masteryXpForNext(wt);
      const frac = Math.min(1, ms.xp / forNext); // 10.9L22 — sem teto: a barra sempre mostra rumo ao PRÓXIMO nível
      const nxt = nextMasteryReward(wt, ms.level);
      // 10.9L22 — passou do último marco (Lv20): não tem mais "MAX", segue subindo pra sempre e
      // cada nível soma um pouco de dano (GameConfig.mastery.pastCapDamagePerLevel).
      const endless = `<div class="m-next">${i18n.t("mastery.endless", { p: (GameConfig.mastery.pastCapDamagePerLevel * 100).toFixed(2) })}</div>`;
      masteryHtml = `<div class="mastery">
        <div class="m-row"><span>${i18n.t("mastery.level")} ${ms.level}</span><span class="m-name">${itemName(inst)}</span></div>
        <div class="bar m-bar"><div class="fill" style="transform:scaleX(${frac})"></div></div>
        ${nxt ? `<div class="m-next">${i18n.t("mastery.nextReward")}: <b>Lv${nxt.level}</b> — ${nxt.desc}</div>` : endless}
      </div>`;
    }

    // 10.9K9 — EVOLUÇÃO: barra de cópias iguais (mesma arma + raridade) que caíram
    let fuseHtml = "";
    if (isWeapon) {
      const ev = inv.evolveInfo(inst.definitionId, inst.rarity, this.playerLevel);
      if (ev.next) {
        const nextName = i18n.t(`rarity.${ev.next}`);
        const frac = Math.min(1, ev.bar / ev.need);
        const nota = ev.blocked
          ? `<div class="d-fuse-row warn">🔒 ${this.esc(i18n.t("evolve.needLevel", { lvl: ev.minLevel, rarity: nextName }))}</div>`
          : "";
        fuseHtml = `<div class="d-fuse">
          <div class="d-fuse-row">${this.esc(i18n.t("evolve.bar", { bar: Math.min(ev.bar, ev.need), need: ev.need, rarity: nextName }))}</div>
          <div class="bar evo-bar"><div class="fill" style="transform:scaleX(${frac})"></div></div>
          ${nota}
          <div class="d-fuse-row">${this.esc(i18n.t("evolve.how"))}</div>
        </div>`;
      } else {
        fuseHtml = `<div class="d-fuse"><div class="d-fuse-row">${this.esc(i18n.t("evolve.maxRarity"))}</div></div>`;
      }
    }

    // ações
    const shards = inv.getShards(inst.definitionId);
    const upCost = inv.upgradeCost(inst);
    const upCoins = inv.upgradeCoins(inst); // 10.9BI
    const canUp = inv.canUpgrade(inst) && this.h.getCoins() >= upCoins;
    const upBtn = Number.isFinite(upCost)
      ? `<button class="btn up ${canUp ? "" : "dim"}" data-a="upgrade">${i18n.t("equipment.upgrade")} (${shards}/${upCost} ${i18n.t("equipment.shards")} · 🪙 ${upCoins})</button>`
      : `<button class="btn up dim" data-a="upgrade">${i18n.t("equipment.maxLevel")}</button>`;
    const actions = isEquipped
      ? `<button class="btn primary" data-a="unequip">${i18n.t("equipment.unequip")}</button>${upBtn}`
      : `<button class="btn primary ${inv.isSlotUnlocked(inst.slot) ? "" : "dim"}" data-a="equip">${inv.isSlotUnlocked(inst.slot) ? i18n.t("equipment.equip") : i18n.t("equipment.slotLocked")}</button>${upBtn}<button class="btn conv" data-a="convert">${i18n.t("equipment.convert")} (+${inv.shardsForDuplicate(inst)})</button>`;
    // 10.9W — MOTIVO do slot bloqueado (o Thiago pediu pra dizer POR QUE está bloqueado)
    const cond = (GameConfig.equipment.slotUnlock as Record<string, string>)[inst.slot] ?? "";
    const slotNote = !isEquipped && !inv.isSlotUnlocked(inst.slot)
      ? `<div class="d-slotnote">🔒 ${this.esc(i18n.t("slot.unlock." + cond))}</div>` : "";

    this.detail.innerHTML = `
      <div class="d-head">
        <div class="d-ic" style="border-color:${c}">${itemSvg(inst)}</div>
        <div class="d-title">
          <div class="d-name" style="color:${c}">${itemName(inst)} ${isEquipped ? `<span class="tag">${i18n.t("equipment.equipped")}</span>` : ""}${inChain ? `<span class="tag chain">⛓ ${i18n.t("equipment.inChain")}</span>` : ""}</div>
          <div class="d-sub">${rarityName(inst)} · ${i18n.t("slot." + def.slot)} · ${i18n.t("equipment.level")} ${inst.level}</div>
        </div>
      </div>
      <div class="d-main"><span class="d-main-lbl">${mainLabel}</span> ${cmpHtml}</div>
      <div class="d-attrs">${lines.join("")}</div>
      ${slotNote}
      ${masteryHtml}
      ${fuseHtml}
      <div class="d-actions">${actions}</div>`;

    this.detail.querySelector('[data-a="equip"]')?.addEventListener("click", () => this.h.onEquip(inst.instanceId));
    this.detail.querySelector('[data-a="unequip"]')?.addEventListener("click", () => this.h.onUnequip(inst.slot));
    this.detail.querySelector('[data-a="upgrade"]')?.addEventListener("click", () => { this.h.onUpgrade(inst.instanceId, this.h.getElixirs() > 0); }); // 10.9P: protege com elixir se tiver
    this.detail.querySelector('[data-a="convert"]')?.addEventListener("click", () => this.h.onConvert(inst.instanceId));
  }
}
